import React, { useState, useEffect } from 'react';
import './CookieBanner.css';
import logo from '../../assets/logo.svg'
import { PrivacyPolicyContent } from '../../Pages/PrivacyPolicy';

const GOOGLE_TAG_ID = 'AW-475092845';

// Enhanced cookie helpers
function setCookie(name, value, days = 365) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

function getCookie(name) {
    return document.cookie.split('; ').reduce((r, v) => {
        const parts = v.split('=');
        return parts[0] === name ? decodeURIComponent(parts[1]) : r;
    }, '');
}

function deleteCookie(name, domain = null, path = '/') {
    const domainStr = domain ? `; domain=${domain}` : '';
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}${domainStr}; SameSite=Lax`;
}

function removeGoogleAnalytics() {
    // Remove GA scripts
    const gaScripts = document.querySelectorAll('script[src*="googletagmanager.com"], script[src*="google-analytics.com"]');
    gaScripts.forEach(script => script.remove());

    // Clear GA global variables (safe: some properties may be non-configurable)
    try {
        if ('gtag' in window) window.gtag = undefined;
    } catch (e) {
        // ignore
    }
    try {
        if ('ga' in window) window.ga = undefined;
    } catch (e) {
        // ignore
    }
    if (window.dataLayer) {
        window.dataLayer = [];
    }

    // Clear GA timers and intervals
    if (window.gaTimers) {
        window.gaTimers.forEach(timer => clearTimeout(timer));
        window.gaTimers = [];
    }
}

function deleteAllGACookies() {
    // Get all existing cookies to find GA cookies dynamically
    const allCookies = document.cookie.split(';');
    const gaCookiesToDelete = [];

    // Static list of known GA cookies
    const knownGACookies = [
        '_ga', '_gid', '_gat',
        '_gcl_au', '_gcl_aw', '_gcl_dc',
        '__utma', '__utmb', '__utmc', '__utmt', '__utmz', '__utmv'
    ];

    // Find all GA cookies (including dynamic ones)
    allCookies.forEach(cookie => {
        const cookieName = cookie.split('=')[0].trim();
        if (cookieName.startsWith('_ga') || cookieName.startsWith('_gid') ||
            cookieName.startsWith('_gat') || cookieName.startsWith('__utm') ||
            cookieName.includes('GA') || cookieName.includes('google') || cookieName.startsWith('_gcl')) {
            gaCookiesToDelete.push(cookieName);
        }
    });

    // Combine static and dynamic cookie lists
    const allGACookies = [...new Set([...knownGACookies, ...gaCookiesToDelete])];

    const currentDomain = window.location.hostname;
    const domains = [
        '',  // No domain (most important)
        currentDomain,
        '.' + currentDomain,
        'localhost',
        '.localhost'
    ];

    // Add parent domains
    const hostParts = currentDomain.split('.');
    if (hostParts.length > 1) {
        domains.push('.' + hostParts.slice(-2).join('.'));
    }
    if (hostParts.length > 2) {
        domains.push('.' + hostParts.slice(-3).join('.'));
    }

    const paths = ['/', '', '/path'];

    // Multiple deletion attempts for each cookie
    allGACookies.forEach(cookieName => {
        // Method 1: Simple deletion (most effective)
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
        document.cookie = `${cookieName}=; Max-Age=0; path=/`;
        document.cookie = `${cookieName}=; Max-Age=0;`;

        // Method 2: Domain and path combinations
        domains.forEach(domain => {
            paths.forEach(path => {
                const domainStr = domain ? `; domain=${domain}` : '';
                const pathStr = path ? `; path=${path}` : '';

                // Multiple deletion methods
                document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT${pathStr}${domainStr}`;
                document.cookie = `${cookieName}=; Max-Age=0${pathStr}${domainStr}`;
                document.cookie = `${cookieName}=deleted; expires=Thu, 01 Jan 1970 00:00:00 GMT${pathStr}${domainStr}`;

                // With SameSite
                document.cookie = `${cookieName}=; Max-Age=0${pathStr}${domainStr}; SameSite=Lax`;
                document.cookie = `${cookieName}=; Max-Age=0${pathStr}${domainStr}; SameSite=Strict`;
                document.cookie = `${cookieName}=; Max-Age=0${pathStr}${domainStr}; SameSite=None; Secure`;
            });
        });
    });

    // Force immediate cleanup with a slight delay to ensure processing
    setTimeout(() => {
        allGACookies.forEach(cookieName => {
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
            document.cookie = `${cookieName}=; Max-Age=0; path=/`;
        });
    }, 100);

    // Additional cleanup after 500ms
    setTimeout(() => {
        allGACookies.forEach(cookieName => {
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        });
    }, 500);
}

function clearAllStorage() {
    // Clear localStorage
    try {
        if (typeof Storage !== 'undefined' && localStorage) {
            const keysToRemove = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && (key.includes('ga') || key.includes('_ga') || key.includes('google') ||
                    key.includes('facebook') || key.includes('fb') || key.includes('marketing'))) {
                    keysToRemove.push(key);
                }
            }
            keysToRemove.forEach(key => localStorage.removeItem(key));
        }
    } catch (e) {
        console.warn('Could not clear localStorage:', e);
    }

    // Clear sessionStorage
    try {
        if (typeof Storage !== 'undefined' && sessionStorage) {
            const keysToRemove = [];
            for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                if (key && (key.includes('ga') || key.includes('_ga') || key.includes('google') ||
                    key.includes('facebook') || key.includes('fb') || key.includes('marketing'))) {
                    keysToRemove.push(key);
                }
            }
            keysToRemove.forEach(key => sessionStorage.removeItem(key));
        }
    } catch (e) {
        console.warn('Could not clear sessionStorage:', e);
    }
}

// Translations
const translations = {
    pt: {
       "Consent to Cookies & Data processing": "Consentimento para Cookies e Processamento de Dados",
        "We use cookies to enhance your experience, analyze traffic, personalize content, and integrate third-party services (e.g. social media, videos, analytics). Your data may be shared with external providers. You can manage or withdraw your consent anytime via Manage Cookies Preferences at the bottom left.": "We use cookies to enhance your experience, analyze traffic, personalize content, and integrate third-party services (e.g. social media, videos, analytics). Your data may be shared with external providers. You can manage or withdraw your consent anytime via Manage Cookies Preferences at the bottom left.",
        "Function": "Função",
        "Measurement": "Medição",
        "Reject all": "Rejeitar todos",
        "Accept all": "Aceitar todos",
        "Save + Exit": "Salvar e Sair",
        "Customize your choice": "Personalizar sua escolha",
        "Cookies": "Cookies",
        "Privacy notice": "Aviso de Privacidade",
        "Manage your cookie preferences": "Gerenciar suas preferências de cookies",
        "Save Preferences": "Salvar Preferências",
        "Strictly Necessary (Always Enabled)": "Estritamente Necessário (Sempre Ativado)",
        "Analytics Cookies": "Cookies de Análise",
        "Marketing Cookies": "Cookies de Marketing",
        "14": "14",
        "Privacy Policy": "Política de Privacidade",
    },        
    en: {
        "Consent to Cookies & Data processing": "Consent to Cookies & Data processing",
        "We use cookies to enhance your experience, analyze traffic, personalize content, and integrate third-party services (e.g. social media, videos, analytics). Your data may be shared with external providers. You can manage or withdraw your consent anytime via Manage Cookies Preferences at the bottom left.": "We use cookies to enhance your experience, analyze traffic, personalize content, and integrate third-party services (e.g. social media, videos, analytics). Your data may be shared with external providers. You can manage or withdraw your consent anytime via Manage Cookies Preferences at the bottom left.",
        "Function": "Function",
        "Measurement": "Measurement",
        "Reject all": "Reject all",
        "Accept all": "Accept all",
        "Save + Exit": "Save + Exit",
        "Customize your choice": "Customize your choice",
        "Cookies": "Cookies",
        "Privacy notice": "Privacy notice",
        "Manage your cookie preferences": "Manage your cookie preferences",
        "Save Preferences": "Save Preferences",
        "Strictly Necessary (Always Enabled)": "Strictly Necessary (Always Enabled)",
        "Analytics Cookies": "Analytics Cookies",
        "Marketing Cookies": "Marketing Cookies",
        "14": "14",
        "Privacy Policy": "Privacy Policy",
    },
};

const CookieBanner = () => {
    const language = navigator.language.slice(0, 2);
    const t = translations[language] || translations.en;

    const [showBanner, setShowBanner] = useState(true); // Show by default for demo
    const [functionChecked, setFunctionChecked] = useState(true); // Always enabled
    const [measurementChecked, setMeasurementChecked] = useState(false); // EU-safe default: non-essential disabled
    const [consentStatus, setConsentStatus] = useState('none');
    const [showSaveButton, setShowSaveButton] = useState(false);
    const [showPrivacyModal, setShowPrivacyModal] = useState(false);

    // Add effect to sync global variables whenever state changes
    useEffect(() => {
        // Set global variables that external scripts can check
        window.HFAnalyticsEnabled = measurementChecked;
        window.HFConsentStatus = consentStatus;

        // Dispatch custom events for external listeners
        window.dispatchEvent(new CustomEvent('cookieConsentChanged', {
            detail: {
                function: functionChecked,
                measurement: measurementChecked,
                consent: consentStatus
            }
        }));
    }, [functionChecked, measurementChecked, consentStatus]);

    // Update showSaveButton when measurementChecked changes
    useEffect(() => {
        setShowSaveButton(!measurementChecked);
    }, [measurementChecked]);

    // Initialize state based on existing cookies
    useEffect(() => {
        const consent = getCookie('HFCookieConsent');
        const measurementConsent = getCookie('HFMeasurementConsent') === 'true';

        if (!consent) {
            setMeasurementChecked(false); // Default to unchecked if no consent cookie
            setShowBanner(true);
            setConsentStatus('none');
        } else {
            setConsentStatus(consent);
            setShowBanner(false); // Hide banner if consent cookie exists
            if (consent === 'custom') {
                setMeasurementChecked(measurementConsent);
            } else if (consent === 'accepted') {
                setMeasurementChecked(true);
            } else if (consent === 'rejected') {
                setMeasurementChecked(false);
            }
        }

        // Load or block analytics based on consent
        if (consent === 'accepted' || (consent === 'custom' && measurementConsent)) {
            loadGoogleAnalytics();
        } else if (consent === 'rejected' || (consent === 'custom' && !measurementConsent)) {
            blockAnalytics();
        }
    }, []);

    const loadGoogleAnalytics = () => {
        window.dataLayer = window.dataLayer || [];
        if (!window.gtag) {
            function gtag() {
                window.dataLayer.push(arguments);
            }
            window.gtag = gtag;
        }

        // Set consent before loading.
        // This banner only offers "Measurement" (analytics) consent, not ads/remarketing.
        window.gtag('consent', 'default', {
            analytics_storage: 'granted',
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
            functionality_storage: 'granted',
            personalization_storage: 'denied',
        });

        const applyConfigs = () => {
            window.gtag('js', new Date());
            window.gtag('config', GOOGLE_TAG_ID);
        };

        // Load gtag script once
        const existingScript = document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${GOOGLE_TAG_ID}"]`);
        if (existingScript) {
            applyConfigs();
            return;
        }

        const script = document.createElement('script');
        script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_TAG_ID}`;
        script.async = true;
        script.onload = applyConfigs;
        document.head.appendChild(script);
    };

    const blockAnalytics = () => {
        // Update consent to denied FIRST
        if (window.gtag) {
            window.gtag('consent', 'update', {
                analytics_storage: 'denied',
                ad_storage: 'denied',
                ad_user_data: 'denied',
                ad_personalization: 'denied',
                functionality_storage: 'denied',
                personalization_storage: 'denied'
            });
        }

        // Remove GA and clean up immediately
        removeGoogleAnalytics();

        // Multiple cleanup attempts with delays to ensure all cookies are caught
        deleteAllGACookies();
        setTimeout(() => deleteAllGACookies(), 50);
        setTimeout(() => deleteAllGACookies(), 150);
        setTimeout(() => deleteAllGACookies(), 300);
        setTimeout(() => deleteAllGACookies(), 600);

        clearAllStorage();
    };

    const handleAcceptAll = () => {
        const newConsent = 'accepted';
        setCookie('HFCookieConsent', newConsent);
        setCookie('HFMeasurementConsent', 'true');

        // Update state immediately and synchronously
        setMeasurementChecked(true);
        setConsentStatus(newConsent);
        setShowBanner(false);

        loadGoogleAnalytics();

        // Force immediate variable updates
        setTimeout(() => {
            setMeasurementChecked(true);
            window.HFAnalyticsEnabled = true;
        }, 0);
    };

    const handleRejectAll = () => {
        const newConsent = 'rejected';
        setCookie('HFCookieConsent', newConsent);
        setCookie('HFMeasurementConsent', 'false');

        // Update state immediately and synchronously
        setMeasurementChecked(false);
        setConsentStatus(newConsent);
        setShowBanner(false);

        // Block and clean up immediately
        blockAnalytics();

        // Force immediate variable updates
        setTimeout(() => {
            setMeasurementChecked(false);
            window.HFAnalyticsEnabled = false;
        }, 0);
    };

    const handleSaveExit = () => {
        const newConsent = 'custom';
        setCookie('HFCookieConsent', newConsent);
        setCookie('HFMeasurementConsent', measurementChecked.toString());

        // Update state immediately and force re-render
        setConsentStatus(newConsent);
        setShowBanner(false);

        // Handle analytics consent changes
        if (measurementChecked) {
            loadGoogleAnalytics();
        } else {
            // If analytics was previously enabled but now disabled, clean up immediately
            blockAnalytics();
        }

        // Force immediate state sync by triggering a re-render
        setTimeout(() => {
            setMeasurementChecked(measurementChecked);
        }, 0);
    };

    const handleCustomizeChoice = () => {
        // No preferences modal to customize
    };

    if (!showBanner) return null;

    return (
        <div className="cookie-banner-overlay">
            {showBanner && (
                <div className="cookie-banner-modal">
                    <div className="cookie-banner-content">
                        <div className="cookie-banner-header">
                        </div>
                        <div className="cookie-banner-logo-container">
                            <img src={logo} alt="" className='cookie-banner-logo' />
                        </div>
                        <h2 className="cookie-banner-title">
                            {t["Consent to Cookies & Data processing"]}
                        </h2>

                        <p className="cookie-banner-text">
                            {t["We use cookies to enhance your experience, analyze traffic, personalize content, and integrate third-party services (e.g. social media, videos, analytics). Your data may be shared with external providers. You can manage or withdraw your consent anytime via Manage Cookies Preferences at the bottom left."]}
                        </p>

                        {/* Cookie categories */}
                        <div className="cookie-categories">
                            <div className="cookie-category-item">
                                <input
                                    type="checkbox"
                                    checked={functionChecked}
                                    disabled
                                    readOnly
                                />
                                <div className="cookie-category-icon">⚙️</div>
                                <span className="cookie-category-label disabled">
                                    {t["Function"]}
                                </span>
                            </div>

                            <div className="cookie-category-item">
                                <input
                                    type="checkbox"
                                    checked={measurementChecked}
                                    onChange={(e) => setMeasurementChecked(e.target.checked)}
                                />
                                <div className="cookie-category-icon">📊</div>
                                <span className="cookie-category-label">
                                    {t["Measurement"]}
                                </span>
                            </div>
                        </div>

                        {/* Buttons */}
                        <div className="cookie-banner-btn-wrapper">
                            <div className="cookie-banner-primary-buttons">
                                <button
                                    className="cookie-banner-decline-btn"
                                    onClick={handleRejectAll}
                                >
                                    ❌ {t["Reject all"]}
                                </button>
                                <button
                                    className="cookie-banner-accept-btn"
                                    onClick={handleAcceptAll}
                                >
                                    ✅ {t["Accept all"]}
                                </button>
                            </div>
                            {showSaveButton && (
                                <div>
                                    <button
                                        className="cookie-save-preference-btn"
                                        onClick={handleSaveExit}
                                    >
                                        {t["Save + Exit"]}
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Footer links */}
                        <div className="cookie-banner-footer">
                            <a href="#" onClick={e => { e.preventDefault(); setShowPrivacyModal(true); }}>{t["Privacy notice"]}</a>
                        </div>
                    </div>
                </div>
            )}
            {showPrivacyModal && (
                <div className="privacy-modal-overlay">
                    <div className="privacy-modal-content">
                        <button onClick={() => setShowPrivacyModal(false)} style={{ fontWeight: 'bold', position: 'absolute', top: '1rem', right: '1rem', fontSize: '1.5rem', background: 'none', border: 'none', cursor: 'pointer', zIndex: 1, textAlign: 'center' }} aria-label="Close">✕</button>                        
                        <div className='pt-4 pb-4'>
                            <img src={logo} alt="" className='cookie-banner-logo' />
                        </div>
                        <h2 className='text-2xl font-bold text-center mb-2' style={{ fontWeight: 'bold', color: '#2c5aa0' }}>
                            {t["Privacy Policy"]}
                        </h2>
                        <PrivacyPolicyContent />
                    </div>
                </div>
            )}
        </div>
    )
}

export default CookieBanner;